import * as Yup from 'yup';
import { TFunction } from 'i18next';
import { FieldErrors, FieldValues } from 'react-hook-form';
import { UI_TEXT } from '../constants';
import { QUERY_TEMPLATES, DEFAULT_QUERY_TEMPLATE, CONFIG_TEMPLATES, DEFAULT_CONFIG_TEMPLATE } from 'constants/analyticsTemplateDocs';

// Not a React component/hook, so callers (component-scope Yup schemas) pass
// their own `t` from useTranslation() rather than this calling the hook itself.
export const isValidJsonOrEmpty = (label: string, t: TFunction) =>
  Yup.string().test('valid-json', `${label} ${t('VALIDATION.INVALID_JSON')}`, (value) => {
    if (!value || value.trim() === '') return true;
    try { JSON.parse(value); return true; }
    catch { return false; }
  });

// Canonical English tab names (Info, Data, Filters, ...) are used across both
// the Visualization and Dashboard config forms as internal comparison keys
// (matching against TEMPLATE_TAB_LABEL / templateWarnTabs state, error-tab
// lookups, etc.) as well as display text. Keep call sites passing the
// canonical string for logic, and only translate at the point of display.
const TAB_NAME_KEYS: Record<string, string> = {
  Info: 'TABS.INFO',
  Data: 'TABS.DATA',
  Filters: 'TABS.FILTERS',
  'Additional Config': 'TABS.ADDITIONAL_CONFIG',
  Access: 'TABS.ACCESS',
  Dashboards: 'TABS.DASHBOARDS',
  Layout: 'TABS.LAYOUT',
  Config: 'TABS.CONFIG',
  Widgets: 'TABS.WIDGETS',
};

export const translateTabName = (name: string, t: TFunction): string =>
  t(TAB_NAME_KEYS[name] ?? name);

export const normalizeJson = (s: string) => (s || '').replace(/\s+/g, ' ').trim();

// Core "is this field still just the starter template?" comparison — shared by
// any form that offers starter templates (Visualizations' chartType-dependent
// templates, Dashboards' fixed per-field templates) so only the lookup of
// *which* template to compare against differs per form.
export const isTemplateUnedited = (value: string, template: string): boolean => {
  if (!value || value.trim() === '') return false;
  return normalizeJson(value) === normalizeJson(template || '');
};

export const isStarterTemplate = (key: string, value: string, chartType: string): boolean => {
  let template = '';
  if (key === 'queries') template = QUERY_TEMPLATES[chartType?.toLowerCase()] ?? DEFAULT_QUERY_TEMPLATE;
  else if (key === 'config') template = CONFIG_TEMPLATES[chartType?.toLowerCase()] ?? DEFAULT_CONFIG_TEMPLATE;
  else if (key === 'kpiFilters') template = UI_TEXT.VISUALIZATION_TEMPLATES.KPI_FILTERS_CONFIG;
  return isTemplateUnedited(value, template);
};

interface SplitConfigResult {
  restConfigStr: string;
  kpiFiltersStr: string;
}

export const splitConfig = (configStr: string): SplitConfigResult => {
  try {
    const parsed = JSON.parse(configStr || '{}');
    const { kpiFilters, ...rest } = parsed;
    return {
      restConfigStr: JSON.stringify(rest, null, 2),
      kpiFiltersStr: JSON.stringify(kpiFilters ?? [], null, 2),
    };
  } catch {
    return { restConfigStr: configStr || '', kpiFiltersStr: '[]' };
  }
};

export const mergeConfig = (configStr: string, kpiFiltersStr: string): string => {
  let restConfig: Record<string, unknown> = {};
  try { restConfig = JSON.parse(configStr || '{}'); } catch { restConfig = {}; }

  let kpiFilters: unknown[] = [];
  try { kpiFilters = JSON.parse(kpiFiltersStr || '[]'); } catch { kpiFilters = []; }

  const merged: Record<string, unknown> = { ...restConfig };
  if (Array.isArray(kpiFilters) && kpiFilters.length > 0) {
    merged.kpiFilters = kpiFilters;
  } else {
    delete merged.kpiFilters;
  }
  return JSON.stringify(merged);
};

export interface ErrorSummaryFieldCheck<T extends FieldValues> {
  field: keyof T;
  tabLabel: string;
  // Set when a field can be invalid for a reason RHF's own schema doesn't
  // capture (e.g. a JSON-parse failure tracked in separate component state).
  // Falls back to `extraMessage` when the field has no RHF error message.
  extraInvalid?: boolean;
  extraMessage?: string;
}

export interface ErrorSummaryExtraCheck {
  label: string;
  message: string;
  passed: boolean;
}

// Joins a form's RHF field errors plus any non-RHF business-rule checks
// (access roles selected, at least one dashboard linked, etc.) into one toast
// message. Shared across forms — each form supplies its own field→tab mapping
// and extra checks, since those genuinely differ per form.
export const buildErrorSummary = <T extends FieldValues>(
  errors: FieldErrors<T>,
  fieldChecks: ErrorSummaryFieldCheck<T>[],
  extraChecks: ErrorSummaryExtraCheck[] = [],
  t: TFunction
): string => {
  const msgs: string[] = [];
  fieldChecks.forEach(({ field, tabLabel, extraInvalid, extraMessage }) => {
    const err = errors[field];
    if (err || extraInvalid) {
      msgs.push(`${translateTabName(tabLabel, t)} → ${(err?.message as string | undefined) || extraMessage || ''}`);
    }
  });
  extraChecks.forEach(({ label, message, passed }) => {
    if (!passed) msgs.push(`${translateTabName(label, t)} → ${message}`);
  });
  return msgs.length
    ? `${t('VALIDATION.FIX_ERRORS_PREFIX')}\n• ${msgs.join('\n• ')}`
    : t('VALIDATION.FIX_ERRORS_GENERIC');
};
